import React, { useState, useContext } from 'react'
import styles from './MeetingView.module.scss'
import { useQuery, useLazyQuery, useMutation } from '@apollo/client'
import {
  GET_MEETINGS_QUERY, GET_MEETING_WITH_CONTENT_QUERY, UPDATE_MEETING_DATE_MUTATION, DELETE_MEETING_MUTATION,
  ADD_MEETING_SENTENCE_MUTATION, ADD_MEETING_WORD_MUTATION,
  DELETE_MEETING_SENTENCE_MUTATION, DELETE_MEETING_WORD_MUTATION,
} from '../query/gqlQueries'
import EditCalendarIcon from '@mui/icons-material/EditCalendar'
import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/Add'
import CheckIcon from '@mui/icons-material/Check'
import CancelIcon from '@mui/icons-material/Cancel'
import UserContext from '../context/UserContext'
import languages from '../languages'
import { speak } from '../speech/speak'
import voices from '../speech/voices'
import StudentLessonView from './StudentLessonView'
import AssignLessonDialog from '../dialog/AssignLessonDialog'
import SchoolIcon from '@mui/icons-material/School'
import CloseIcon from '@mui/icons-material/Close'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import FormatQuoteIcon from '@mui/icons-material/FormatQuote'
import AbcIcon from '@mui/icons-material/Abc'
import AssignmentIcon from '@mui/icons-material/Assignment'
import RefreshIcon from '@mui/icons-material/Refresh'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import ListIcon from '@mui/icons-material/List'

export default function MeetingView({ setIsVisible, voiceSpeed, onPracticeMeetingContent }) {
  const { username } = useContext(UserContext)
  const [selectedMeetingId, setSelectedMeetingId] = useState(null)
  const [studentLesson, setStudentLesson] = useState(null) // parsed lessonData → StudentLessonView
  const [studentMeetingId, setStudentMeetingId] = useState(null) // for SRS id-resolution
  const [assignFor, setAssignFor] = useState(null) // { id, title } → AssignLessonDialog
  const [isEditingDate, setIsEditingDate] = useState(false)
  const [localDate, setLocalDate] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [viewMode, setViewMode] = useState('calendar') // 'list' | 'calendar'
  const [calDate, setCalDate] = useState(() => { const n = new Date(); return { y: n.getFullYear(), m: n.getMonth() } })

  // Fetch all meetings
  const { data: meetingsData, loading: meetingsLoading, error: meetingsError, refetch: refetchMeetings } = useQuery(GET_MEETINGS_QUERY, {
    variables: { username },
    skip: !username
  })

  // Lazy query for meeting detail
  const [fetchMeetingContent, { data: contentData, loading: contentLoading, error: contentError, refetch: refetchContent }] = useLazyQuery(GET_MEETING_WITH_CONTENT_QUERY)

  // Date update mutation
  const [updateMeetingDate, { loading: savingDate }] = useMutation(UPDATE_MEETING_DATE_MUTATION)

  // Delete meeting mutation (cascades to children)
  const [deleteMeeting, { loading: deletingMeeting }] = useMutation(DELETE_MEETING_MUTATION)

  // Per-item content editing.
  const [editMode, setEditMode] = useState(false)
  const [newSent, setNewSent] = useState({ source: '', target: '' })
  const [newWord, setNewWord] = useState({ source: '', target: '' })
  const [addMeetingSentence, { loading: addingSentence }] = useMutation(ADD_MEETING_SENTENCE_MUTATION)
  const [addMeetingWord, { loading: addingWord }] = useMutation(ADD_MEETING_WORD_MUTATION)
  const [deleteMeetingSentence] = useMutation(DELETE_MEETING_SENTENCE_MUTATION)
  const [deleteMeetingWord] = useMutation(DELETE_MEETING_WORD_MUTATION)

  const meetings = meetingsData?.getMeetings || []

  const handleSelectMeeting = (identity) => {
    setSelectedMeetingId(identity)
    setIsEditingDate(false)
    fetchMeetingContent({ variables: { identity } })
  }

  const handleBack = () => {
    setSelectedMeetingId(null)
    setIsEditingDate(false)
    setConfirmDelete(false)
    setEditMode(false)
    setNewSent({ source: '', target: '' })
    setNewWord({ source: '', target: '' })
  }

  const handleDeleteMeeting = async (identity) => {
    await deleteMeeting({ variables: { identity } })
    setConfirmDelete(false)
    setSelectedMeetingId(null)
    refetchMeetings()
  }

  const handleSpeak = (e, text, lang) => {
    e.stopPropagation()
    speak(text, voices[lang], voiceSpeed)
  }

  // New items inherit the meeting's dominant non-English language, so you don't
  // have to pick a language for every add in a single-language class.
  const inferSourceLang = () => {
    const c = contentData?.getMeetingWithContent
    const langs = [...(c?.sentences || []), ...(c?.words || [])]
      .map((p) => p.source?.lang).filter((l) => l && l !== 'eng')
    if (!langs.length) return 'eng'
    const counts = {}
    langs.forEach((l) => { counts[l] = (counts[l] || 0) + 1 })
    return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0]
  }

  const handleAddSentence = async () => {
    if (!newSent.source.trim()) return
    await addMeetingSentence({ variables: { input: {
      meetingId: selectedMeetingId, sourceText: newSent.source.trim(),
      targetText: newSent.target.trim(), sourceLang: inferSourceLang(), targetLang: 'eng', username,
    } } })
    setNewSent({ source: '', target: '' })
    refetchContent({ identity: selectedMeetingId })
  }

  const handleAddWord = async () => {
    if (!newWord.source.trim()) return
    await addMeetingWord({ variables: { input: {
      meetingId: selectedMeetingId, word: newWord.source.trim(),
      translation: newWord.target.trim(), lang: inferSourceLang(), username,
    } } })
    setNewWord({ source: '', target: '' })
    refetchContent({ identity: selectedMeetingId })
  }

  const handleDeleteSentence = async (sentenceId) => {
    await deleteMeetingSentence({ variables: { sentenceId } })
    refetchContent({ identity: selectedMeetingId })
  }

  const handleDeleteWord = async (wordId) => {
    await deleteMeetingWord({ variables: { meetingId: selectedMeetingId, wordId } })
    refetchContent({ identity: selectedMeetingId })
  }

  // Robust date formatter — handles 'YYYY-MM-DD', ISO strings, and Neo4j DateTime objects
  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    try {
      // Neo4j DateTime objects may be returned as objects with year/month/day fields
      if (typeof dateStr === 'object' && dateStr.year) {
        const y = dateStr.year.low ?? dateStr.year
        const m = String((dateStr.month?.low ?? dateStr.month) || 1).padStart(2, '0')
        const d = String((dateStr.day?.low ?? dateStr.day) || 1).padStart(2, '0')
        return new Date(`${y}-${m}-${d}`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
      }
      // Plain YYYY-MM-DD — parse as local date to avoid timezone shift
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [y, m, d] = dateStr.split('-').map(Number)
        return new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
      }
      // ISO string
      const parsed = new Date(dateStr)
      if (isNaN(parsed.getTime())) return ''
      return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    } catch {
      return ''
    }
  }

  // Prefer the actual meeting date over the node creation date
  const displayDate = (meeting) =>
    meeting.properties.meetingDate || meeting.properties.dateAdded

  // Normalise any date shape (Neo4j object, ISO, YYYY-MM-DD) to a local
  // 'YYYY-MM-DD' key for bucketing meetings into calendar cells.
  const dayKey = (dateStr) => {
    if (!dateStr) return null
    try {
      if (typeof dateStr === 'object' && dateStr.year) {
        const y = dateStr.year.low ?? dateStr.year
        const m = String((dateStr.month?.low ?? dateStr.month) || 1).padStart(2, '0')
        const d = String((dateStr.day?.low ?? dateStr.day) || 1).padStart(2, '0')
        return `${y}-${m}-${d}`
      }
      if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) return dateStr.slice(0, 10)
      const p = new Date(dateStr)
      if (isNaN(p.getTime())) return null
      return `${p.getFullYear()}-${String(p.getMonth() + 1).padStart(2, '0')}-${String(p.getDate()).padStart(2, '0')}`
    } catch { return null }
  }

  const shiftMonth = (delta) => setCalDate((d) => {
    const m = d.m + delta
    if (m < 0) return { y: d.y - 1, m: 11 }
    if (m > 11) return { y: d.y + 1, m: 0 }
    return { y: d.y, m }
  })
  const goToday = () => { const n = new Date(); setCalDate({ y: n.getFullYear(), m: n.getMonth() }) }

  // Start editing — pre-fill with existing meetingDate or today
  const handleStartEdit = (meeting) => {
    setLocalDate(meeting.properties.meetingDate || new Date().toISOString().split('T')[0])
    setIsEditingDate(true)
  }

  const handleSaveDate = async (identity) => {
    if (!localDate) return
    await updateMeetingDate({ variables: { identity, meetingDate: localDate } })
    setIsEditingDate(false)
    refetchContent({ identity })
  }

  const getPriorityClass = (priority) => {
    const p = (priority || '').toLowerCase()
    if (p === 'high') return styles.priorityHigh
    if (p === 'low') return styles.priorityLow
    return styles.priorityMedium
  }

  if (meetingsLoading) {
    return (
      <div className={styles.meetingView}>
        <div className={styles.gridBg} />
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner} />
          <div className={styles.loadingText}>Loading meetings...</div>
        </div>
      </div>
    )
  }

  if (meetingsError) {
    return (
      <div className={styles.meetingView}>
        <div className={styles.gridBg} />
        <div className={styles.status}>Error loading meetings: {meetingsError.message}</div>
      </div>
    )
  }

  // ── DETAIL VIEW ──────────────────────────────────────────────────────────────
  // A meeting imported via "Import Lesson" carries the full structured lesson in
  // properties.lessonData — parse it and hand off to the student experience.
  const openLesson = (raw, meetingId) => {
    try { const l = JSON.parse(raw || ''); if (l && Array.isArray(l.sections)) { setStudentLesson(l); setStudentMeetingId(meetingId || null) } }
    catch { /* not a structured lesson — ignore */ }
  }

  if (studentLesson) {
    return <StudentLessonView lesson={studentLesson} meetingId={studentMeetingId} voiceSpeed={voiceSpeed} onClose={() => { setStudentLesson(null); setStudentMeetingId(null) }} />
  }

  if (selectedMeetingId) {
    const meetingContent = contentData?.getMeetingWithContent
    const meeting = meetingContent?.meeting
    const sentencePairs = meetingContent?.sentences || []
    const wordPairs = meetingContent?.words || []
    const tasks = meetingContent?.tasks || []

    const convertPairsToFlashcards = (pairs) => {
      return pairs.map(p => ({
        sourceId: p.source?.id || Math.random().toString(),
        source: p.source?.text,
        sourceLanguage: p.source?.lang,
        // Pass mnemonic image fields through so meeting-spawned flashcards
        // show saved images. These are null on sentence pairs (only word
        // nodes have imageUrl populated).
        sourceImageUrl: p.source?.imageUrl || null,
        sourceImageDescription: p.source?.imageDescription || null,
        translations: p.target ? [{
          targetId: p.target?.id || Math.random().toString(),
          target: p.target?.text,
          targetLanguage: p.target?.lang || 'eng'
        }] : []
      }))
    }

    return (
      <div className={styles.meetingView}>
        <div className={styles.gridBg} />
        <button className={styles.closeBtn} onClick={() => setIsVisible(false)}>
          <CloseIcon />
        </button>

        <div className={styles.container}>
          {contentLoading ? (
            <div className={styles.loadingState}>
              <div className={styles.loadingSpinner} />
              <div className={styles.loadingText}>Loading meeting content...</div>
            </div>
          ) : contentError ? (
            <div className={styles.status}>Error: {contentError.message}</div>
          ) : meeting ? (
            <>
              <div className={styles.detailHeader}>
                <button className={styles.backBtn} onClick={handleBack}>
                  <ArrowBackIcon fontSize="small" /> Back
                </button>
                <div className={styles.detailHeaderActions}>
                  {meeting.properties.lessonData && (
                    <>
                      <button
                        className={styles.practiceAllBtn}
                        onClick={() => openLesson(meeting.properties.lessonData, meeting.identity)}
                        title="Open this class as a student — sections, exercises, reading"
                      >
                        <SchoolIcon fontSize="small" /> Open as student
                      </button>
                      {meeting.properties.username === username && (
                        <button
                          className={styles.practiceAllBtn}
                          onClick={() => setAssignFor({ id: meeting.identity, title: meeting.properties.title })}
                          title="Set this lesson as homework for specific students"
                        >
                          <AssignmentIcon fontSize="small" /> Assign as homework
                        </button>
                      )}
                      <AssignLessonDialog meeting={assignFor} onClose={() => setAssignFor(null)} />
                    </>
                  )}
                  {(sentencePairs.length > 0 || wordPairs.length > 0) && (
                    <button
                      className={styles.practiceAllBtn}
                      onClick={() => {
                        if (onPracticeMeetingContent) {
                          onPracticeMeetingContent(convertPairsToFlashcards([...sentencePairs, ...wordPairs]))
                        }
                      }}
                    >
                      Practice All
                    </button>
                  )}
                  <button
                    className={`${styles.practiceAllBtn} ${editMode ? styles.editActive : ''}`}
                    onClick={() => setEditMode((v) => !v)}
                    title="Add or delete sentences and words"
                  >
                    {editMode ? <><CheckIcon fontSize="small" /> Done</> : <><EditIcon fontSize="small" /> Edit</>}
                  </button>
                  {confirmDelete ? (
                    <span className={styles.deleteConfirm}>
                      <span className={styles.deleteConfirmText}>Delete meeting and all content?</span>
                      <button
                        className={`${styles.deleteConfirmBtn} ${styles.deleteConfirmYes}`}
                        onClick={() => handleDeleteMeeting(meeting.identity)}
                        disabled={deletingMeeting}
                      >
                        {deletingMeeting ? 'Deleting…' : 'Delete'}
                      </button>
                      <button
                        className={styles.deleteConfirmBtn}
                        onClick={() => setConfirmDelete(false)}
                        disabled={deletingMeeting}
                      >
                        Cancel
                      </button>
                    </span>
                  ) : (
                    <button
                      className={styles.deleteBtn}
                      onClick={() => setConfirmDelete(true)}
                      title="Delete meeting and all its content"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </button>
                  )}
                </div>
              </div>

              <header className={styles.header}>
                <div className={styles.titleGroup}>
                  <div className={styles.label}>Meeting Notes</div>
                  <h1 className={styles.title}>
                    {meeting.properties.title}
                  </h1>
                  <div className={styles.detailMeta}>
                    <span className={`${styles.metaChip} ${styles.metaType}`}>
                      {meeting.properties.type}
                    </span>

                    {isEditingDate ? (
                      <span className={styles.dateEditor}>
                        <input
                          type="date"
                          className={styles.dateInput}
                          value={localDate}
                          onChange={(e) => setLocalDate(e.target.value)}
                          autoFocus
                        />
                        <button
                          className={styles.dateActionBtn}
                          onClick={() => handleSaveDate(meeting.identity)}
                          disabled={savingDate}
                          title="Save date"
                        >
                          <CheckIcon style={{ fontSize: 16 }} />
                        </button>
                        <button
                          className={styles.dateActionBtn}
                          onClick={() => setIsEditingDate(false)}
                          title="Cancel"
                        >
                          <CancelIcon style={{ fontSize: 16 }} />
                        </button>
                      </span>
                    ) : (
                      <button
                        className={`${styles.metaChip} ${styles.metaDate} ${styles.metaDateClickable}`}
                        onClick={() => handleStartEdit(meeting)}
                        title="Click to edit meeting date"
                      >
                        {formatDate(displayDate(meeting)) || 'Set date'}
                        <EditCalendarIcon style={{ fontSize: 13, marginLeft: 5, opacity: 0.7 }} />
                      </button>
                    )}
                  </div>
                </div>
                <div className={styles.stats}>
                  <div className={styles.statItem}>
                    <span className={styles.statLabel}>Sentences</span>
                    <span className={styles.statValue}>{sentencePairs.length}</span>
                  </div>
                  <div className={styles.statItem}>
                    <span className={styles.statLabel}>Words</span>
                    <span className={styles.statValue}>{wordPairs.length}</span>
                  </div>
                  <div className={styles.statItem}>
                    <span className={styles.statLabel}>Tasks</span>
                    <span className={styles.statValue}>{tasks.length}</span>
                  </div>
                </div>
              </header>

              <div className={styles.contentLayout}>

                {/* ── Left: Sentences ───────────────────────────────── */}
                <div className={`${styles.section} ${styles.sentencesPane}`}>
                  <div className={styles.sectionHeader}>
                    <div className={styles.sectionTitle}>
                      <span className={styles.sectionIcon}><FormatQuoteIcon fontSize="small" /></span>
                      Sentences
                      <span className={styles.sectionCount}>{sentencePairs.length}</span>
                    </div>
                    {sentencePairs.length > 0 && (
                      <button 
                        className={styles.practiceSectionBtn}
                        onClick={() => {
                          if (onPracticeMeetingContent) {
                            onPracticeMeetingContent(convertPairsToFlashcards(sentencePairs))
                          }
                        }}
                      >
                        Practice Sentences
                      </button>
                    )}
                  </div>
                  {sentencePairs.length > 0 || editMode ? (
                    <div className={styles.sentenceList}>
                      {sentencePairs.map((pair, i) => (
                        <div className={styles.sentenceRow} key={pair.source.id || i}>
                          <button
                            className={styles.speakBtn}
                            onClick={(e) => handleSpeak(e, pair.source.text, pair.source.lang)}
                          >
                            <VolumeUpIcon fontSize="small" />
                          </button>
                          <span className={styles.sentenceSource}>{pair.source.text}</span>
                          <span className={styles.sentenceArrow}>→</span>
                          <span className={styles.sentenceTarget}>
                            {pair.target?.text || '—'}
                          </span>
                          <span className={styles.sentenceLang}>
                            {languages[pair.source.lang] || pair.source.lang}
                          </span>
                          {editMode && (
                            <button
                              className={styles.rowDeleteBtn}
                              title="Delete this sentence"
                              onClick={() => handleDeleteSentence(pair.source.id)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </button>
                          )}
                        </div>
                      ))}
                      {editMode && (
                        <div className={styles.addRow}>
                          <input
                            className={styles.addInput}
                            value={newSent.source}
                            onChange={(e) => setNewSent((s) => ({ ...s, source: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleAddSentence() }}
                            placeholder={`New sentence (${languages[inferSourceLang()] || inferSourceLang()})`}
                          />
                          <span className={styles.sentenceArrow}>→</span>
                          <input
                            className={styles.addInput}
                            value={newSent.target}
                            onChange={(e) => setNewSent((s) => ({ ...s, target: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleAddSentence() }}
                            placeholder="English"
                          />
                          <button
                            className={styles.addBtn}
                            onClick={handleAddSentence}
                            disabled={!newSent.source.trim() || addingSentence}
                            title="Add sentence"
                          >
                            <AddIcon fontSize="small" />
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className={styles.emptyPane}>No sentences recorded</div>
                  )}
                </div>

                {/* ── Right column: Words + Tasks ───────────────────── */}
                <div className={styles.rightPane}>

                  {/* Words — top 2/3 */}
                  <div className={`${styles.section} ${styles.wordsPane}`}>
                    <div className={styles.sectionHeader}>
                      <div className={styles.sectionTitle}>
                        <span className={styles.sectionIcon}><AbcIcon fontSize="small" /></span>
                        Vocabulary
                        <span className={styles.sectionCount}>{wordPairs.length}</span>
                      </div>
                      {wordPairs.length > 0 && (
                        <button 
                          className={styles.practiceSectionBtn}
                          onClick={() => {
                            if (onPracticeMeetingContent) {
                              onPracticeMeetingContent(convertPairsToFlashcards(wordPairs))
                            }
                          }}
                        >
                          Practice Words
                        </button>
                      )}
                    </div>
                    {wordPairs.length > 0 || editMode ? (
                      <div className={styles.wordGrid}>
                        {wordPairs.map((pair, i) => (
                          <div className={`${styles.wordChip} ${editMode ? styles.wordChipEditing : ''}`} key={pair.source.id || i}>
                            <button
                              className={styles.speakBtn}
                              onClick={(e) => handleSpeak(e, pair.source.text, pair.source.lang)}
                            >
                              <VolumeUpIcon style={{ fontSize: 14 }} />
                            </button>
                            <span className={styles.wordSource}>{pair.source.text}</span>
                            <span className={styles.wordSep}>=</span>
                            <span className={styles.wordTarget}>
                              {pair.target?.text || '—'}
                            </span>
                            {editMode && (
                              <button
                                className={styles.chipDeleteBtn}
                                title="Remove from this meeting"
                                onClick={() => handleDeleteWord(pair.source.id)}
                              >
                                <CloseIcon style={{ fontSize: 13 }} />
                              </button>
                            )}
                          </div>
                        ))}
                        {editMode && (
                          <div className={`${styles.wordChip} ${styles.addWordChip}`}>
                            <input
                              className={styles.addWordInput}
                              value={newWord.source}
                              onChange={(e) => setNewWord((w) => ({ ...w, source: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleAddWord() }}
                              placeholder="word"
                            />
                            <span className={styles.wordSep}>=</span>
                            <input
                              className={styles.addWordInput}
                              value={newWord.target}
                              onChange={(e) => setNewWord((w) => ({ ...w, target: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleAddWord() }}
                              placeholder="English"
                            />
                            <button
                              className={styles.addBtn}
                              onClick={handleAddWord}
                              disabled={!newWord.source.trim() || addingWord}
                              title="Add word"
                            >
                              <AddIcon style={{ fontSize: 15 }} />
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className={styles.emptyPane}>No vocabulary recorded</div>
                    )}
                  </div>

                  {/* Tasks — bottom 1/3 */}
                  <div className={`${styles.section} ${styles.tasksPane}`}>
                    <div className={styles.sectionHeader}>
                      <div className={styles.sectionTitle}>
                        <span className={styles.sectionIcon}><AssignmentIcon fontSize="small" /></span>
                        Tasks
                      </div>
                      <span className={styles.sectionCount}>{tasks.length}</span>
                    </div>
                    {tasks.length > 0 ? (
                      <div className={styles.taskList}>
                        {tasks.map((task) => (
                          <div className={styles.taskCard} key={task.id}>
                            <span className={`${styles.taskPriority} ${getPriorityClass(task.priority)}`}>
                              {task.priority || 'Medium'}
                            </span>
                            <div className={styles.taskContent}>
                              <div className={styles.taskTitle}>{task.title}</div>
                              {task.description && (
                                <div className={styles.taskDescription}>{task.description}</div>
                              )}
                              {task.type && (
                                <div className={styles.taskType}>{task.type}</div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={styles.emptyPane}>No tasks recorded</div>
                    )}
                  </div>

                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    )
  }

  // ── Calendar month grid (Monday-first) ──────────────────────────────────────
  const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
  const meetingsByDay = {}
  meetings.forEach((mtg) => {
    const k = dayKey(displayDate(mtg))
    if (!k) return
    ;(meetingsByDay[k] = meetingsByDay[k] || []).push(mtg)
  })
  const firstOfMonth = new Date(calDate.y, calDate.m, 1)
  const mondayOffset = (firstOfMonth.getDay() + 6) % 7 // 0 = Monday
  const daysInMonth = new Date(calDate.y, calDate.m + 1, 0).getDate()
  const weeks = Math.ceil((mondayOffset + daysInMonth) / 7)
  const calCells = Array.from({ length: weeks * 7 }, (_, i) => new Date(calDate.y, calDate.m, i - mondayOffset + 1))
  const todayKey = dayKey(new Date())
  const monthLabel = firstOfMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })

  const typeChipClass = (type) => {
    const t = (type || '').toLowerCase()
    if (t.includes('language') || t.includes('class')) return styles.calMeetingLang
    if (t.includes('business')) return styles.calMeetingBiz
    if (t.includes('workshop')) return styles.calMeetingShop
    return ''
  }

  // ── LIST VIEW ────────────────────────────────────────────────────────────────
  return (
    <div className={styles.meetingView}>
      <div className={styles.gridBg} />
      <button className={styles.closeBtn} onClick={() => setIsVisible(false)}>
        <CloseIcon />
      </button>

      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.titleGroup}>
            <div className={styles.label}>Knowledge Base</div>
            <h1 className={styles.title}>
              Meeting <span>Notes</span>
            </h1>
          </div>
          <div className={styles.stats}>
            <div className={styles.viewToggle}>
              <button
                className={`${styles.viewToggleBtn} ${viewMode === 'list' ? styles.viewToggleActive : ''}`}
                onClick={() => setViewMode('list')}
              >
                <ListIcon fontSize="small" /> List
              </button>
              <button
                className={`${styles.viewToggleBtn} ${viewMode === 'calendar' ? styles.viewToggleActive : ''}`}
                onClick={() => setViewMode('calendar')}
              >
                <CalendarMonthIcon fontSize="small" /> Calendar
              </button>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Total</span>
              <span className={styles.statValue}>{meetings.length}</span>
            </div>
            <button
              className={styles.refreshBtn}
              onClick={() => refetchMeetings()}
              title="Refresh meetings"
            >
              <RefreshIcon fontSize="small" />
            </button>
          </div>
        </header>

        {viewMode === 'calendar' ? (
          <div className={styles.calendarWrap}>
            <div className={styles.calNav}>
              <button className={styles.calNavBtn} onClick={() => shiftMonth(-1)} title="Previous month">
                <ArrowBackIcon fontSize="small" />
              </button>
              <div className={styles.calMonthLabel}>{monthLabel}</div>
              <button className={styles.calNavBtn} onClick={() => shiftMonth(1)} title="Next month">
                <ArrowForwardIosIcon fontSize="small" />
              </button>
              <button className={styles.calTodayBtn} onClick={goToday}>Today</button>
            </div>

            <div className={styles.calWeekdays}>
              {WEEKDAYS.map((d) => <div key={d} className={styles.calWeekday}>{d}</div>)}
            </div>

            <div className={styles.calGrid} style={{ gridTemplateRows: `repeat(${weeks}, minmax(0, 1fr))` }}>
              {calCells.map((cell, i) => {
                const key = `${cell.getFullYear()}-${String(cell.getMonth() + 1).padStart(2, '0')}-${String(cell.getDate()).padStart(2, '0')}`
                const inMonth = cell.getMonth() === calDate.m
                const isToday = key === todayKey
                const dayMeetings = meetingsByDay[key] || []
                return (
                  <div
                    key={i}
                    className={`${styles.calCell} ${inMonth ? '' : styles.calCellOut} ${isToday ? styles.calCellToday : ''}`}
                  >
                    <div className={styles.calDayNum}>{cell.getDate()}</div>
                    <div className={styles.calCellMeetings}>
                      {dayMeetings.map((mtg) => (
                        <button
                          key={mtg.identity}
                          className={`${styles.calMeeting} ${typeChipClass(mtg.properties.type)}`}
                          onClick={() => handleSelectMeeting(mtg.identity)}
                          title={`${mtg.properties.title} · ${mtg.properties.type}`}
                        >
                          {mtg.properties.title}
                        </button>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ) : meetings.length === 0 ? (
          <div className={styles.emptyState}>
            <p>No meetings recorded yet.</p>
            <p>Use the Meeting Document importer to add your first meeting.</p>
          </div>
        ) : (
          <div className={styles.meetingList}>
            {meetings.map((meeting) => (
              <div
                className={styles.meetingCard}
                key={meeting.identity}
                onClick={() => handleSelectMeeting(meeting.identity)}
              >
                <div className={styles.meetingCardLeft}>
                  <div className={styles.meetingCardTitle}>{meeting.properties.title}</div>
                  <div className={styles.meetingCardMeta}>
                    <span className={styles.meetingCardType}>{meeting.properties.type}</span>
                    <span className={styles.meetingCardDate}>
                      {formatDate(displayDate(meeting))}
                    </span>
                  </div>
                </div>
                {meeting.properties.lessonData && (
                  <button
                    className={styles.practiceAllBtn}
                    style={{ padding: '0.35rem 0.7rem', marginRight: '0.6rem', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}
                    onClick={(e) => { e.stopPropagation(); openLesson(meeting.properties.lessonData, meeting.identity) }}
                    title="Open this class as a student"
                  >
                    <SchoolIcon fontSize="small" /> Student
                  </button>
                )}
                <div className={styles.meetingCardArrow}>
                  <ArrowForwardIosIcon fontSize="small" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
